import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RuntimeTruthCard } from "@/components/RuntimeTruthBanner";

import { ApprovedFieldsSection } from "./ApprovedFieldsSection";
import { CreateFieldFlow } from "./CreateFieldFlow";
import { FieldUsageView } from "./FieldUsageView";
import { LaunchReadinessSection } from "./LaunchReadinessSection";
import {
  OrganizationWorkspaceSection,
  OrganizationWorkspaceTab,
} from "./OrganizationWorkspaceSection";
import { OrganizationsSection } from "./OrganizationsSection";
import { ResponsibilitiesSection } from "./ResponsibilitiesSection";
import { UsersAccessSection } from "./UsersAccessSection";
import { WorkflowsSection } from "./WorkflowsSection";
import { WORKFLOW_DIRTY_KEY } from "./useTenantWorkflowWorkspace";
import { Pill, SuperAdminScreen } from "./shared";
import { useSuperAdminControlCenter } from "./useSuperAdminControlCenter";

const SCREEN_LABELS: Array<{ id: SuperAdminScreen; label: string }> = [
  { id: "fields", label: "Approved fields" },
  { id: "workflows", label: "Workflows" },
  { id: "organizations", label: "Organizations" },
  { id: "responsibilities", label: "My responsibilities" },
];

const WORKSPACE_OPEN_KEY = "super_admin_organization_workspace_open";
const WORKSPACE_TAB_KEY = "super_admin_organization_workspace_tab";
const SCREEN_STORAGE_KEY = "super_admin_screen";

function readWorkspaceOpen() {
  if (typeof window === "undefined") return false;
  const storedScreen = sessionStorage.getItem(SCREEN_STORAGE_KEY);
  if (storedScreen === "launch" || storedScreen === "users") return true;
  return sessionStorage.getItem(WORKSPACE_OPEN_KEY) === "true";
}

function readWorkspaceTab(): OrganizationWorkspaceTab {
  if (typeof window === "undefined") return "overview";
  const storedScreen = sessionStorage.getItem(SCREEN_STORAGE_KEY);
  if (storedScreen === "launch") return "publication-readiness";
  if (storedScreen === "users") return "users-access";
  const stored = sessionStorage.getItem(WORKSPACE_TAB_KEY);
  return stored === "publication-readiness" || stored === "users-access"
    ? stored
    : "overview";
}

export function SuperAdminPage() {
  const state = useSuperAdminControlCenter();
  const [organizationWorkspaceOpen, setOrganizationWorkspaceOpenState] =
    useState(readWorkspaceOpen);
  const [organizationWorkspaceTab, setOrganizationWorkspaceTabState] =
    useState<OrganizationWorkspaceTab>(readWorkspaceTab);

  function setOrganizationWorkspaceOpen(open: boolean) {
    setOrganizationWorkspaceOpenState(open);
    sessionStorage.setItem(WORKSPACE_OPEN_KEY, String(open));
  }

  function setOrganizationWorkspaceTab(tab: OrganizationWorkspaceTab) {
    setOrganizationWorkspaceTabState(tab);
    sessionStorage.setItem(WORKSPACE_TAB_KEY, tab);
  }

  return (
    <div className="mx-auto flex w-full max-w-[1360px] flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-950">Platform Control Center</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Launch organizations, approved fields, workflows, staff access, and audit evidence in
            plain language.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => void state.loadPage()}>
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Active organization
            </div>
            <div className="mt-1 text-lg font-semibold">
              {state.selectedOrganization?.name ?? "No organization selected"}
            </div>
            <div className="text-sm text-gray-600">
              {state.selectedOrganization?.slug ??
                "Create or select an organization to continue."}
            </div>
          </div>
          <label className="text-sm font-medium">
            Switch organization
            <select
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              value={state.selectedTenantId ?? ""}
              onChange={(event) => {
                const organization = state.organizations.find(
                  (item) => item.id === Number(event.target.value)
                );
                if (
                  organization &&
                  (sessionStorage.getItem(WORKFLOW_DIRTY_KEY) !== "true" ||
                    window.confirm("Discard the unsaved workflow changes?"))
                ) {
                  void state.selectOrganization(organization);
                }
              }}
            >
              <option value="">Select organization</option>
              {state.organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name} · {organization.is_active ? "Active" : "Inactive"}
                </option>
              ))}
            </select>
          </label>
        </CardContent>
      </Card>

      <RuntimeTruthCard />

      <div className="grid gap-3 xl:grid-cols-[210px_1fr]">
        <nav
          aria-label="Super Admin sections"
          className="flex gap-2 overflow-x-auto rounded-lg border bg-white p-2 xl:flex-col xl:overflow-visible"
        >
          {SCREEN_LABELS.map((item) => (
            <Button
              key={item.id}
              type="button"
              variant={state.screen === item.id ? "default" : "outline"}
              size="sm"
              className="justify-start whitespace-nowrap"
              onClick={() => {
                if (item.id === "organizations") {
                  setOrganizationWorkspaceOpen(false);
                }
                state.setScreen(item.id);
              }}
            >
              {item.label}
            </Button>
          ))}
        </nav>

        <div className="min-w-0 space-y-3">
          {state.screen === "create-field" ? (
            <Pill tone="blue">Creating field</Pill>
          ) : null}
          {state.screen === "field-usage" && state.selectedField ? (
            <Pill tone="blue">{state.selectedField.label} usage</Pill>
          ) : null}
          {state.error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {state.error}
            </div>
          ) : null}
          {state.message ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              {state.message}
            </div>
          ) : null}
          {state.saving ? <div className="text-sm text-gray-500">Saving change...</div> : null}

          <div className="overflow-hidden rounded-lg border bg-white">
            {state.loading ? (
              <div className="p-6 text-sm text-gray-600">
                Loading Platform Control Center...
              </div>
            ) : (
              renderScreen()
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function renderScreen() {
    if (state.screen === "create-field") {
      return (
        <CreateFieldFlow
          form={state.fieldForm}
          fields={state.fields}
          saving={state.saving}
          duplicateOverride={state.duplicateOverride}
          onBack={() => state.setScreen("fields")}
          onChange={state.setFieldForm}
          onOverrideChange={state.setDuplicateOverride}
          onSubmit={() => void state.createField()}
        />
      );
    }

    if (state.screen === "field-usage") {
      return (
        <FieldUsageView
          field={state.selectedField}
          usage={state.selectedFieldUsage}
          loading={state.fieldUsageLoading}
          onBack={() => state.setScreen("fields")}
        />
      );
    }

    if (state.screen === "workflows") {
      return (
        <WorkflowsSection
          tenantId={state.selectedTenantId}
          tenantName={state.selectedOrganization?.name}
          workflowTemplates={state.workflowTemplateLibrary}
          scholarshipStandards={state.scholarshipStandards}
          olderReusableSetups={state.reviewTemplates}
          selectedOlderSetupUsage={state.selectedTemplateUsage}
          olderSetupUsageLoading={state.templateUsageLoading}
          retiringOlderSetup={state.retiringTemplate}
          fundingOptions={state.fundingPolicyOptions}
          renewalOptions={state.renewalPolicyOptions}
          mentoringCriteria={state.mentoringCriteriaOptions}
          reusableSetupForm={state.scholarshipStandardForm}
          savingReusableSetup={state.saving}
          onReusableSetupFormChange={state.setScholarshipStandardForm}
          onSaveReusableSetup={state.saveScholarshipStandard}
          onOlderSetupUsage={(setup) => void state.openTemplateUsage(setup)}
          onOlderSetupEdit={state.editTemplateDraft}
          onOlderSetupActivate={(setup) => void state.activateTemplateAction(setup)}
          onOlderSetupVersion={(setup) => void state.createTemplateVersion(setup)}
          onOlderSetupRetireStart={(setup) => void state.startRetireTemplate(setup)}
          onOlderSetupRetireConfirm={() => void state.retireTemplateAction()}
        />
      );
    }

    if (state.screen === "organizations") {
      if (organizationWorkspaceOpen && state.selectedOrganization) {
        return (
          <OrganizationWorkspaceSection
            organization={state.selectedOrganization}
            activeProgramCount={state.activeProgramCount}
            staffCount={state.staff.length}
            activeTab={organizationWorkspaceTab}
            onTabChange={setOrganizationWorkspaceTab}
            onBackToList={() => setOrganizationWorkspaceOpen(false)}
            publicationReadiness={
              <LaunchReadinessSection
                projection={state.projection}
                recentChanges={state.projection?.change_history ?? []}
                staffCount={state.staff.length}
                saving={state.saving}
                onProvisionReviewProcess={state.provisionSelectedReviewProcess}
                showHeader={false}
              />
            }
            usersAccess={
              <UsersAccessSection
                staff={state.staff}
                form={state.staffForm}
                usernameAvailability={state.usernameAvailability}
                onFormChange={state.setStaffForm}
                onCreate={() => void state.createStaffUserAction()}
                onRoleChange={(userId, role) => void state.changeRole(userId, role)}
                onApproveAccess={(userId) => void state.approveStaffAccess(userId)}
                onVerify={(userId) => void state.verifyStaff(userId)}
                onReset={(userId) => void state.resetAccess(userId)}
                onSuspend={(userId) => void state.suspendStaff(userId)}
                onReactivate={(userId) => void state.reactivateStaff(userId)}
                showHeader={false}
              />
            }
          />
        );
      }

      return (
        <OrganizationsSection
          organizations={state.organizations}
          form={state.organizationForm}
          activeTenantId={state.selectedTenantId}
          activeProgramCount={state.activeProgramCount}
          onFormChange={state.setOrganizationForm}
          onCreate={() => void state.createOrganizationAction()}
          onSelect={async (organization) => {
            await state.selectOrganization(organization);
            setOrganizationWorkspaceTab("publication-readiness");
            setOrganizationWorkspaceOpen(true);
            state.setScreen("organizations");
          }}
          onDeactivate={(organization) => void state.deactivateOrganizationAction(organization)}
        />
      );
    }

    if (state.screen === "responsibilities") {
      return <ResponsibilitiesSection />;
    }

    return (
      <ApprovedFieldsSection
        fields={state.fields}
        onCreate={() => state.setScreen("create-field")}
        onUsage={(field) => void state.openUsage(field)}
        onRetire={(field) => void state.retireField(field)}
        retireReason={state.fieldRetireReason}
        onRetireReasonChange={state.setFieldRetireReason}
      />
    );
  }
}
